import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import {
  HUBSPOT_OBJECTS,
  HUBSPOT_OBJECT_LABEL,
  HUBSPOT_PROPERTY_REGISTRY,
  type HubSpotObject,
} from "@colab/shared";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Two-step cmdk picker: first the user chooses a HubSpot object (Deal,
// Contact, etc.), then the property list for that object opens. Picking
// either with the keyboard or mouse closes the popover.
//
// The picker is fully controlled — `value` is the current mapping (or
// `null` if the field is unmapped), and `onChange(null)` clears.
//
// Ports components/admin/forms/CrmMappingCombobox.tsx; the only divergence
// is the shared-package import path for the HubSpot catalog.

export type CrmMappingValue = {
  object: HubSpotObject;
  property: string;
};

export function CrmMappingCombobox({
  value,
  onChange,
}: {
  value: CrmMappingValue | null;
  onChange: (next: CrmMappingValue | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"object" | "property">(
    value ? "property" : "object",
  );
  const [pickedObject, setPickedObject] = useState<HubSpotObject | null>(
    value?.object ?? null,
  );

  const display = value
    ? `${HUBSPOT_OBJECT_LABEL[value.object]} · ${propertyLabel(value)}`
    : null;

  function reset() {
    setStep("object");
    setPickedObject(null);
  }

  return (
    <div className="flex items-center gap-1.5">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setStep(value ? "property" : "object");
            setPickedObject(value?.object ?? null);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {display ? (
              <span className="truncate text-left">{display}</span>
            ) : (
              <span className="text-muted-foreground">Pick a HubSpot property…</span>
            )}
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          {step === "object" || !pickedObject ? (
            <Command>
              <CommandInput placeholder="Search HubSpot object…" />
              <CommandList>
                <CommandEmpty>No object found.</CommandEmpty>
                <CommandGroup heading="HubSpot object">
                  {HUBSPOT_OBJECTS.map((obj) => (
                    <CommandItem
                      key={obj}
                      value={obj}
                      onSelect={() => {
                        setPickedObject(obj);
                        setStep("property");
                      }}
                    >
                      {HUBSPOT_OBJECT_LABEL[obj]}
                      {value?.object === obj && (
                        <Check className="ml-auto h-3.5 w-3.5" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <Command>
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {HUBSPOT_OBJECT_LABEL[pickedObject]} · property
                </span>
                <button
                  type="button"
                  onClick={reset}
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Change object
                </button>
              </div>
              <CommandInput placeholder="Search property…" />
              <CommandList>
                <CommandEmpty>No property found.</CommandEmpty>
                <CommandGroup>
                  {HUBSPOT_PROPERTY_REGISTRY[pickedObject].map((p) => (
                    <CommandItem
                      key={p.property}
                      value={`${p.label} ${p.property}`}
                      onSelect={() => {
                        onChange({ object: pickedObject, property: p.property });
                        setOpen(false);
                      }}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{p.label}</span>
                        <span className="truncate font-mono text-[10px] text-muted-foreground">
                          {p.property}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "ml-2 rounded-sm px-1 text-[9px] uppercase tracking-wider text-muted-foreground",
                          "bg-muted/60",
                        )}
                      >
                        {p.type}
                      </span>
                      {value?.object === pickedObject &&
                        value.property === p.property && (
                          <Check className="ml-2 h-3.5 w-3.5" />
                        )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>

      {value && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Clear mapping"
          onClick={() => onChange(null)}
          className="shrink-0 text-muted-foreground hover:text-destructive"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function propertyLabel(value: CrmMappingValue): string {
  const list = HUBSPOT_PROPERTY_REGISTRY[value.object];
  return list.find((p) => p.property === value.property)?.label ?? value.property;
}
